import { NgFor, NgIf, NgStyle } from "@angular/common";
import { Component, Inject, OnInit, signal } from "@angular/core";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from "@angular/material/bottom-sheet";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { formatDecimalNumbersInString } from "@core/currency-display.util";
import { SlidesElement } from "@interfaces/slides";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
@Component({
  selector: "app-offerOfDayDetail",
  templateUrl: "./offerOfDayDetail.component.html",
  styleUrls: ["./offerOfDayDetail.component.scss"],
  standalone: true,
  imports: [
    IonicModule,
    NgStyle,
    NgIf,
    NgFor,
    TranslatePipe,
  ],
})
export class OfferOfDayDetailComponent implements OnInit {
  selectedLanguage: string = "en";
  languageCodeSignal = signal<string>("")
  slideUpDetails: Data;
  preferredCurrency: string = "GBP";
  preferredCurrencySignal = signal<string>("GBP");
  currencySymbol: string = "£";
  public languageCode: string;
  flashSaleStyleString = "flash_sale";
  userId: string;
  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA)
    public slidesElement: SlidesElement,
    private analytics: AngularFireAnalytics,
    private bottomSheetRef: MatBottomSheetRef<OfferOfDayDetailComponent>,
    public translate: TranslateService,
    private authService: AuthService,
    private GTMSegment: GTMSegmentService
  ) {
    this.preferredCurrency = "GBP";
    this.currencySymbol = "£";
    this.languageCode = "en";
    this.GTMSegment.GTMPopupInitialization("OfferOfDay");
    // Subscribe to selectedlanguage$ observable
    this.authService.selectedlanguage$.subscribe({
      next: (l: string) => {
        // Set selectedLanguage to the received value or default to 'en'
        this.selectedLanguage = l || "en";
        // If l is falsy, return to avoid executing further code
        if (!l) return;
        // Set the language for translation
        translate.use(l || "en");
      },
      error: (err) => {
        return err;
      }
    });


    // Subscribe to userDetails$ observable
    this.authService.userDetails$.subscribe({
      next: (user: Data) => {
        // Extract language code from user data or default to 'en'
        this.languageCodeSignal.set(user?.language_code || "en")
        this.languageCode = this.languageCodeSignal();
        // Set the language for translation
        translate.use(this.languageCodeSignal() || "en");

        // Extract preferred currency from user data or default to 'GBP'
        this.preferredCurrencySignal.set(user?.preferred_currency || "GBP")
        this.preferredCurrency = this.preferredCurrencySignal();

        // Subscribe to getCurrencyList$ observable
        this.authService.getCurrencyList$.subscribe({
          next: (data: Data) => {
            // Find currency details based on preferred currency
            const CurrencyDetails = data?.details?.find((x: Data) => x?.currency_code === this.preferredCurrencySignal());
            // Extract currency symbol from currency details or default to '£'
            this.currencySymbol = CurrencyDetails?.currency_symbol || "£";
          },
          error: (err) => {
            throw new Error(err);
          }
        });
      },
      error: (err) => {
        return err;
      }
    });


    this.authService.currencyNodeDvData = slidesElement?.slideDetails?.slide_info;
    this.authService.getDVFromString(slidesElement?.slideDetails?.slide_up?.content?.[this.languageCode]?.description || slidesElement?.slideDetails?.slide_up?.description)
      .then((dvString: string) => {
        if (dvString) {
          this.slideUpDetails = dvString
            ?.split("<li>")
            .slice(1)
            .map((x) => {
              x = this.formatNumbersInString(x);
              return x?.split("</li>")[0];
            });
        } else {
          return ''
        }
      }).catch((err) => {
        // Fire-and-forget: the returned Promise is intentionally not awaited.
        // errorHandlerLogs is deferred into .then() so any synchronous throw it makes is
        // caught by the subsequent .catch(), preventing a secondary unhandled rejection.
        void Promise.resolve()
          .then(() => this.authService?.errorHandlerLogs?.('Error in getDVFromString (offerOfDayDetail):', err))
          .catch((logErr) => {
            try { console.error('getDVFromString (offerOfDayDetail) logging failed:', logErr, 'original error:', err); } catch { /* ignore */ }
          });
        return '';
      });
  }

  async ngOnInit() {
    await this.authService
      ?.getUid("OfferOfDayDetailComponent")
      ?.then((uid) => {
        this.userId = uid;
        this.analytics.logEvent("offer_of_the_day_modal", {
          userId: this.userId,
        });
      })
      .catch((error: any) => {
        return error;
      });
  }

  makePayment() {
    if (!this.slideUpDetails) return;
    this.bottomSheetRef.dismiss(this.slideUpDetails);
  }

  getUrl(url: string) {
    return "url('" + url + "')";
  }

  back() {
    this.bottomSheetRef.dismiss();
  }

  private formatNumbersInString(originalString: string): string {
    return formatDecimalNumbersInString(originalString);
  }
}
